import { GearSix, SignOut, UserSwitch } from "@phosphor-icons/react";
import {
  Button,
  Card,
  Flex,
  Modal,
  Space,
  notification,
  Menu,
  MenuProps,
} from "antd";
import Cookies from "js-cookie";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { colors } from "../../../styles/theme";
import { useImpersonation } from "../../../contexts/impersonation-context/ImpersonationContext";
import { ImpersonationModal } from "../impersonation-modal/ImpersonationModal";

const CostumMenu = styled(Menu)`
  &.ant-menu {
    border-right: none !important;
    padding: 0;
  }

  & .ant-menu-item {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 48px !important;
    height: 48px !important;
    min-height: 48px !important;
    padding: 0 !important;
    margin: 0 auto 8px auto !important;
    line-height: 48px !important;

    &:last-child {
      margin-bottom: 0 !important;
    }

    .ant-menu-title-content {
      display: none !important;
    }

    .ant-menu-item-icon {
      margin: 0 !important;
      margin-inline-end: 0 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 24px !important;
      height: 24px !important;
      color: var(--color-text) !important;

      svg {
        display: block !important;
        width: 24px !important;
        height: 24px !important;
        color: inherit !important;
        fill: currentColor !important;
        stroke: currentColor !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
    }

    &.ant-menu-item-selected {
      background-color: ${colors.primaryLight} !important;
      border-radius: 8px;

      .ant-menu-item-icon {
        color: ${colors.primary} !important;

        svg {
          color: ${colors.primary} !important;
        }
      }
    }

    &:hover {
      background-color: ${colors.backgroundHover} !important;
      border-radius: 8px;
    }

    /* Special styling for impersonation menu item when active */
    &.impersonation-active {
      background-color: rgba(245, 158, 11, 0.1) !important;
      border-radius: 8px;

      .ant-menu-item-icon {
        color: ${colors.warning} !important;

        svg {
          color: ${colors.warning} !important;
        }
      }
    }
  }
`;

// Styled menu dla mobile/expanded - wyrównane do lewej z etykietami
const ExpandedMenu = styled(Menu)`
  & .ant-menu-item {
    padding: 12px 24px;
    margin-bottom: 8px;
    height: auto;
  }

  border: none;
`;

export const UserInfo: React.FC<{ refetch: any; collapsed: any }> = ({
  refetch,
  collapsed,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [modal1Open, setModal1Open] = useState(false);
  const [impersonationModalOpen, setImpersonationModalOpen] = useState(false);
  const { isDeveloper, isImpersonating } = useImpersonation();

  const [logoutIsLoading, setLogoutIsLoading] = useState(false);
  const logout = async () => {
    setLogoutIsLoading(true);
    try {
      Cookies.remove("accessToken");
      Cookies.remove("role");
      await refetch();
      navigate({
        pathname: location.pathname,
        search: "",
      });
      notification.success({
        message: t("userInfo.loggedOut"),
        style: {
          background: colors.successBackground,
        },
      });
    } catch (error) {
      notification.error({
        message: t("userInfo.logoutError"),
        style: {
          background: colors.errorBackground,
        },
      });
      console.error("Wystąpił błąd podczas wylogowywania", error);
      setLogoutIsLoading(false);
    }
  };

  type MenuItem = Required<MenuProps>["items"][number];

  function getItem(
    label: React.ReactNode,
    key: React.Key,
    path: string,
    icon?: React.ReactNode,
    children?: MenuItem[]
  ): MenuItem {
    return {
      key,
      icon,
      children,
      label,
      onClick: () => {
        path && navigate(path);
      },
    } as MenuItem;
  }

  const isSettingsActive = location.pathname.startsWith("/settings");

  // Impersonation item for developers (shown above Settings)
  const getImpersonationItem = (): MenuItem => {
    return {
      key: 'impersonation',
      icon: <UserSwitch size={24} weight="regular" />,
      label: t("userInfo.switchAccount"),
      onClick: () => {
        setImpersonationModalOpen(true);
      },
      className: isImpersonating ? 'impersonation-active' : undefined
    } as MenuItem;
  };

  const items: MenuItem[] = isDeveloper
    ? [
        getImpersonationItem(),
        getItem(t("userInfo.settings"), "settings", "settings", <GearSix size={24} weight="regular" />),
      ]
    : [
        getItem(t("userInfo.settings"), "settings", "settings", <GearSix size={24} weight="regular" />),
      ];

  const logoutItem: MenuItem = {
    key: "logout",
    icon: <SignOut size={24} weight="regular" />,
    label: t("userInfo.logout"),
    onClick: () => logout(),
    danger: true,
  } as MenuItem;

  return (
    <>
      <ImpersonationModal
        open={impersonationModalOpen}
        onClose={() => setImpersonationModalOpen(false)}
      />
      <div>
        {collapsed ? (
          <>
            {/* Desktop collapsed - tylko ikony */}
            <CostumMenu
              theme="light"
              mode="inline"
              items={items}
              selectedKeys={isSettingsActive ? ["settings"] : []}
            />
            <div
              style={{
                padding: "8px",
                width: "100%",
              }}
            >
              <Flex justify="center">
                <Button
                  type="text"
                  danger
                  loading={logoutIsLoading}
                  onClick={() => logout()}
                  icon={<SignOut size={24} weight="regular" />}
                  style={{
                    width: "48px",
                    height: "48px",
                    minWidth: "48px",
                    minHeight: "48px",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              </Flex>
            </div>
          </>
        ) : (
          <>
            {/* Mobile expanded - menu z labelami */}
            <ExpandedMenu
              theme="light"
              mode="inline"
              items={[...items, logoutItem]}
              selectedKeys={isSettingsActive ? ["settings"] : []}
              style={{ border: 'none' }}
            />
          </>
        )}
        <Modal
          maskClosable={false}
          title={t("userInfo.notifications")}
          style={{ top: 20 }}
          open={modal1Open}
          okText={t("userInfo.readAll")}
          cancelText={t("userInfo.close")}
          onOk={() => setModal1Open(false)}
          onCancel={() => setModal1Open(false)}
        >
          {new Array(10).fill("x").map((e) => {
            return (
              <Card
                key={e}
                title="Pushes open the window"
                size="small"
                style={{
                  marginBottom: "20px",
                }}
                extra={<a href="#">{t("userInfo.more")}</a>}
              >
                <Space
                  style={{
                    marginBottom: "20px",
                  }}
                >
                  {t("userInfo.notificationText")}
                </Space>
                <Flex justify="start">
                  <Button size="small" type="primary">
                    {t("userInfo.read")}
                  </Button>
                </Flex>
              </Card>
            );
          })}
        </Modal>
      </div>
    </>
  );
};
